import React, { useState } from "react";
import { Instagram, Mail, Menu, X } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useSiteData } from "../context/SiteDataContext";
import { DEFAULT_NAV_ITEMS, getContactNavigation, getVisibleNavigationItems } from "../lib/navigation";
import { getSection } from "../lib/sectionStyles";

const NavItem = ({ to, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      `font-masthead text-base tracking-[0.22em] uline px-1 py-1 ${
        isActive ? "text-[var(--red)]" : "text-[var(--ink)]"
      }`
    }
  >
    {label}
  </NavLink>
);

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { writer, siteSections } = useSiteData();
  const section = getSection(siteSections, "global", "global.header", {
    title: "THE WRITING PORTFOLIO.",
    label: writer.shortName,
    ctaLabel: "Contact",
    ctaUrl: "/contact",
    content: {
      nav: DEFAULT_NAV_ITEMS,
      contactVisible: true,
    },
  });

  if (!section) return null;

  const nav = getVisibleNavigationItems(section.content?.nav).map((item) => ({ ...item, to: item.url }));
  const contact = getContactNavigation(section);
  const desktopPrimary = nav.filter((item) => ["/", "/journalist-portfolio", "/about"].includes(item.to));
  const beatLinks = nav.filter((item) =>
    ["/fashion-journalist", "/music-journalist", "/cultural-commentary", "/interviews"].includes(item.to)
  );

  return (
    <header className="sticky top-0 z-40 bg-[var(--paper)]/92 backdrop-blur-sm border-b-2 border-[var(--ink)]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-baseline gap-3 group" onClick={() => setIsMobileMenuOpen(false)}>
          <span className="font-masthead text-[20px] sm:text-[24px] leading-none tracking-[0.04em] flex items-baseline gap-1.5">
            {(section.title || "THE WRITING PORTFOLIO.").split(/\s+/).map((word, index, words) => (
              <span
                key={`${word}-${index}`}
                className={index === words.length - 1 ? "text-[var(--red)] relative" : "text-[var(--ink)]"}
              >
                {word}
                {index === words.length - 1 ? (
                  <span className="absolute -bottom-1 left-0 right-0 h-1 bg-[var(--pink)]" aria-hidden="true" />
                ) : null}
              </span>
            ))}
          </span>
          <span className="hidden sm:inline font-mono text-[10px] tracking-[0.3em] text-[var(--ink-soft)] uppercase">
            - {section.label || writer.shortName}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {desktopPrimary.map((link) => (
            <NavItem key={link.to} to={link.to} label={link.label} />
          ))}
          <div className="hidden xl:flex items-center gap-5">
            {beatLinks.map((link) => (
              <NavItem key={link.to} to={link.to} label={link.label} />
            ))}
          </div>
          {contact.isVisible ? (
            <NavItem to={contact.url} label={contact.label} />
          ) : null}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={writer.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="w-9 h-9 grid place-items-center border-2 border-[var(--ink)] hover:bg-[var(--red)] hover:text-[var(--pink-soft)] hover:border-[var(--red)] transition-colors"
          >
            <Instagram size={16} strokeWidth={2} />
          </a>
          <a
            href={`mailto:${writer.email}`}
            aria-label="Email"
            className="hidden sm:grid w-9 h-9 place-items-center bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--red)] transition-colors"
          >
            <Mail size={16} strokeWidth={2} />
          </a>
          <button
            type="button"
            aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
            className="md:hidden w-9 h-9 grid place-items-center border-2 border-[var(--ink)] hover:bg-[var(--red)] hover:text-[var(--pink-soft)] hover:border-[var(--red)] transition-colors"
          >
            {isMobileMenuOpen ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
          </button>
        </div>
      </div>
      {isMobileMenuOpen ? (
        <nav
          id="mobile-navigation"
          aria-label="Mobile navigation"
          className="md:hidden border-t-2 border-[var(--ink)] px-5 py-4 grid grid-cols-2 gap-x-5 gap-y-4 bg-[var(--paper)]"
        >
          {nav.map((link) => (
            <NavItem
              key={link.to}
              to={link.to}
              label={link.label}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          ))}
          {contact.isVisible ? (
            <NavItem
              to={contact.url}
              label={contact.label}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          ) : null}
        </nav>
      ) : null}
    </header>
  );
};

export default Header;
